import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime, t, type MessageKey } from '@aquabox/shared';
import {
  APPROVAL_DECISIONS,
  rejectNoteLength,
  useApprovalDecision,
  useApprovals,
  type ApprovalItem,
  type ApprovalKind,
} from '../api/approvals';

/**
 * Onaylar ekranı — bekleyen BÜTÜN kalemler tek listede.
 *
 * Tür süzgeci istemci tarafındadır: sunucu `GET /api/approvals` ucu `kind` parametresi
 * kabul etmez, yanıt zaten tam liste gelir. Ekranda HİÇBİR karar hesaplanmaz — onay/ret
 * gövdesi ve gerekçe eşiği ilgili modülün şemasındadır, panel yalnız aynı gövdeyi gönderir.
 *
 * Karar ucu olmayan türlerde (CASH_HANDOVER, STOCK_CORRECTION, MORNING_LOAD) satırda yalnız
 * "detaya git" bağlantısı gösterilir.
 */

/** Tür etiketleri — `panel.approvals.kind.*` anahtarları. */
const KIND_LABELS: Record<ApprovalKind, MessageKey> = {
  RELOAD: 'panel.approvals.kind.RELOAD',
  CASH_HANDOVER: 'panel.approvals.kind.CASH_HANDOVER',
  STOCK_CORRECTION: 'panel.approvals.kind.STOCK_CORRECTION',
  MORNING_LOAD: 'panel.approvals.kind.MORNING_LOAD',
  ORDER_TRANSFER: 'panel.approvals.kind.ORDER_TRANSFER',
  DELIVERY_DEVIATION: 'panel.approvals.kind.DELIVERY_DEVIATION',
  TRIP_AUDIT: 'panel.approvals.kind.TRIP_AUDIT',
};

/** Müşteri adı: sipariş bağlantısından gelen ad (telefon/adres yanıtta YOK). */
function customerName(item: ApprovalItem): string {
  return item.order?.customer?.fullName ?? item.order?.customerName ?? '';
}

/** Plaka: seferin aracı, yoksa siparişin aracı, yoksa devir hedef aracı. */
function plate(item: ApprovalItem): string {
  return item.trip?.vehicle?.plate ?? item.order?.vehicle?.plate ?? item.targetVehicle?.plate ?? '';
}

/**
 * Karar düğmesi etiketleri — türün karar anlamına göre değişir.
 *
 * Duruş denetiminde onay "incelendi", ret "şüphe doğru" demektir; genel "Onayla/Reddet"
 * etiketleri bu türde ne yapıldığını söylemez.
 */
interface DecisionLabels {
  approve: MessageKey;
  reject: MessageKey;
  confirm: MessageKey;
  reason: MessageKey;
}

function decisionLabels(kind: ApprovalKind): DecisionLabels {
  if (kind === 'TRIP_AUDIT') {
    return {
      approve: 'panel.approvals.tripAudit.approve',
      reject: 'panel.approvals.tripAudit.reject',
      confirm: 'panel.approvals.tripAudit.confirm',
      reason: 'panel.approvals.tripAudit.rejectReason',
    };
  }
  return {
    approve: 'panel.approvals.approve',
    reject: 'panel.approvals.reject',
    confirm: 'panel.approvals.rejectConfirm',
    reason: 'panel.approvals.rejectReason',
  };
}

export function Approvals() {
  const query = useApprovals();
  const decision = useApprovalDecision();
  const navigate = useNavigate();
  const [kind, setKind] = useState<ApprovalKind | 'ALL'>('ALL');
  /** Reddetme gerekçesi yazılan kalem ve gerekçe metni. */
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const items = query.data?.items ?? [];
  const kinds = Array.from(new Set(items.map((i) => i.kind)));
  /**
   * Seçili tür listeden DÜŞTÜYSE süzgeç "Tümü"ye döner.
   *
   * Aksi halde son kalemi karara bağlanan türde liste boş kalır ve select boş değer gösterir
   * (kullanıcı ne olduğunu anlamaz, ekran "onay yok" sanır).
   */
  const activeKind = kind !== 'ALL' && !kinds.includes(kind) ? 'ALL' : kind;
  const visible = activeKind === 'ALL' ? items : items.filter((i) => i.kind === activeKind);
  /** Sunucunun toplamı listeden büyükse kuyruk kesilmiş demektir. */
  const morePending = Math.max(0, (query.data?.total ?? 0) - items.length);

  /**
   * Kararı gönderir; BAŞARILI olursa form kapanır.
   *
   * Hata olursa form AÇIK kalır ve gerekçe metni korunur — patron yazdığı gerekçeyi
   * yeniden yazmaz. Hata `decision.isError` ile ekranda gösterilir.
   */
  async function decide(item: ApprovalItem, approve: boolean): Promise<void> {
    try {
      await decision.mutateAsync({
        item,
        approve,
        ...(approve ? {} : { note: notes[item.id] ?? '' }),
      });
      setRejectingId(null);
      setNotes((cur) => {
        const next = { ...cur };
        delete next[item.id];
        return next;
      });
    } catch {
      // Hata: form açık kalır, metin korunur (hata mesajı ekranda gösterilir).
    }
  }

  const errorText =
    decision.error instanceof Error ? decision.error.message : t('panel.approvals.decisionError');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p className="muted-line" data-testid="approvals-intro">
        {t('approvals.intro')}
      </p>
      {/* Tek tür varsa süzgeç gürültüdür; en az iki tür olunca görünür. */}
      <div
        style={{
          display: kinds.length > 1 ? 'flex' : 'none',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5 }}>
          {t('panel.approvals.filterLabel')}
          <select
            className="input"
            aria-label={t('panel.approvals.filterLabel')}
            data-testid="approvals-kind-filter"
            value={activeKind}
            onChange={(e) => setKind(e.target.value as ApprovalKind | 'ALL')}
          >
            <option value="ALL">{t('panel.approvals.filterAll')}</option>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {t(KIND_LABELS[k])}
              </option>
            ))}
          </select>
        </label>
      </div>

      {decision.isError ? (
        <p role="alert" style={{ margin: 0, color: 'var(--danger)' }}>
          {errorText}
        </p>
      ) : null}

      {query.isPending ? (
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
      ) : query.isError ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <p role="alert" style={{ margin: 0, color: 'var(--danger)' }}>
            {t('panel.approvals.loadError')}
          </p>
          <button className="btn btn-secondary" onClick={() => void query.refetch()}>
            {t('panel.approvals.retry')}
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div data-testid="approvals-empty" style={{ margin: 0 }}>
          <p style={{ margin: 0 }}>{t('panel.approvals.empty')}</p>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            {t('panel.approvals.emptyHint')}
          </p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          {morePending > 0 ? (
            <p
              data-testid="approvals-more-pending"
              style={{ margin: '0 0 8px', color: 'var(--text-secondary)', fontSize: 13 }}
            >
              {t('panel.approvals.morePending', { count: morePending })}
            </p>
          ) : null}
          <table
            className="approvals-table"
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}
          >
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th>{t('panel.approvals.colKind')}</th>
                <th>{t('panel.approvals.colRequestedAt')}</th>
                <th>{t('panel.approvals.colCustomer')}</th>
                <th>{t('panel.approvals.colPlate')}</th>
                <th>{t('panel.approvals.colDetail')}</th>
                <th>{t('panel.approvals.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => {
                const spec = APPROVAL_DECISIONS[item.kind];
                const labels = decisionLabels(item.kind);
                const note = notes[item.id] ?? '';
                const rejecting = rejectingId === item.id;
                // Gerekçe ölçümü SUNUCUNUN kuralıyla aynı (boşluksuz karakter sayımı dahil).
                const noteTooShort =
                  spec !== null && rejectNoteLength(spec, note) < spec.minRejectNote;
                return (
                  <tr
                    key={item.id}
                    data-testid="approvals-row"
                    style={{ borderBottom: '1px solid var(--border)', verticalAlign: 'top' }}
                  >
                    <td style={{ whiteSpace: 'nowrap' }}>{t(KIND_LABELS[item.kind])}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(item.createdAt)}</td>
                    <td>{customerName(item) || t('panel.approvals.unknown')}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {plate(item) || t('panel.approvals.unknown')}
                    </td>
                    <td>
                      <span>{item.title}</span>
                      {item.detail ? (
                        <span style={{ display: 'block', color: 'var(--text-secondary)' }}>
                          {item.detail}
                        </span>
                      ) : null}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {spec ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-primary"
                            data-testid="approvals-approve"
                            disabled={decision.isPending}
                            onClick={() => void decide(item, true)}
                          >
                            {t(labels.approve)}
                          </button>{' '}
                          <button
                            type="button"
                            className="btn"
                            data-testid="approvals-reject"
                            disabled={decision.isPending}
                            onClick={() => setRejectingId(rejecting ? null : item.id)}
                          >
                            {t(labels.reject)}
                          </button>{' '}
                        </>
                      ) : null}
                      {item.orderId ? (
                        <button
                          type="button"
                          className="btn"
                          data-testid="approvals-go-order"
                          onClick={() => void navigate(`/siparisler?order=${item.orderId}`)}
                        >
                          {t('panel.approvals.goToOrder')}
                        </button>
                      ) : null}
                      {rejecting ? (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            alignItems: 'flex-end',
                            marginTop: 8,
                          }}
                        >
                          <textarea
                            className="input"
                            aria-label={t(labels.reason)}
                            data-testid="approvals-reject-note"
                            value={note}
                            onChange={(e) =>
                              setNotes((cur) => ({ ...cur, [item.id]: e.target.value }))
                            }
                          />
                          {spec && noteTooShort ? (
                            <span style={{ fontSize: 12.5, color: 'var(--danger)' }}>
                              {t('panel.approvals.rejectReasonShort', {
                                n: spec.minRejectNote,
                              })}
                            </span>
                          ) : null}
                          <button
                            type="button"
                            className="btn"
                            data-testid="approvals-reject-confirm"
                            disabled={!spec || noteTooShort || decision.isPending}
                            onClick={() => void decide(item, false)}
                          >
                            {t(labels.confirm)}
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
